import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_THEME = {
  primary_color: '#228B22',      // Forest green (buttons)
  secondary_color: '#90EE90',    // Light green (accent)
  accent_color: '#2E8B57',       // Sea green (hover)
  background_color: '#F0FFF0',   // Honeydew - light green background
  text_color: '#1A1A1A',         // Black (text)
  heading_font: 'Playfair Display',
  body_font: 'Inter',
  navbar_style: 'floating',
  footer_style: 'full',
  button_style: 'rounded',
  card_style: 'elevated',
  enable_animations: true,
  enable_grain_overlay: true
};

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  loading: true
});

export const useTheme = () => useContext(ThemeContext);

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const response = await axios.get(`${API}/theme`);
        if (response.data) {
          setTheme({ ...DEFAULT_THEME, ...response.data });
        }
      } catch (error) {
        console.log('Using default theme');
      } finally {
        setLoading(false);
      }
    };
    fetchTheme();
  }, []);

  // Apply theme as CSS variables
  useEffect(() => {
    if (loading) return;

    const root = document.documentElement;
    
    // Colors
    root.style.setProperty('--color-primary', theme.primary_color);
    root.style.setProperty('--color-secondary', theme.secondary_color);
    root.style.setProperty('--color-accent', theme.accent_color);
    root.style.setProperty('--color-background', theme.background_color);
    root.style.setProperty('--color-text', theme.text_color);
    
    // Map to existing CSS variable names used in tailwind
    root.style.setProperty('--burgundy', theme.primary_color);
    root.style.setProperty('--gold', theme.secondary_color);
    root.style.setProperty('--purple', theme.accent_color);
    root.style.setProperty('--cream', theme.background_color);
    root.style.setProperty('--charcoal', theme.text_color);
    
    // Fonts - load Google Fonts dynamically
    const fontLink = document.getElementById('dynamic-fonts');
    if (fontLink) {
      fontLink.href = `https://fonts.googleapis.com/css2?family=${theme.heading_font.replace(/ /g, '+')}:wght@400;500;600;700&family=${theme.body_font.replace(/ /g, '+')}:wght@400;500;600&display=swap`;
    } else {
      const link = document.createElement('link');
      link.id = 'dynamic-fonts';
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${theme.heading_font.replace(/ /g, '+')}:wght@400;500;600;700&family=${theme.body_font.replace(/ /g, '+')}:wght@400;500;600&display=swap`;
      document.head.appendChild(link);
    }
    
    root.style.setProperty('--font-heading', `'${theme.heading_font}', serif`);
    root.style.setProperty('--font-body', `'${theme.body_font}', sans-serif`);
    
    // Animation toggle
    if (!theme.enable_animations) {
      root.style.setProperty('--animation-duration', '0s');
      root.classList.add('no-animations');
    } else {
      root.style.removeProperty('--animation-duration');
      root.classList.remove('no-animations');
    }
    
  }, [theme, loading]);

  return (
    <ThemeContext.Provider value={{ theme, loading }}>
      {children}
    </ThemeContext.Provider>
  );
}

export default ThemeContext;
